/*
 * Typed endpoint helpers — thin wrappers over the openapi-fetch client that
 * return unwrapped, fully-typed payloads (throwing `ApiError` on non-2xx).
 *
 * These are the functions auth flows and TanStack Query hooks call. Keeping
 * them here (rather than inline in components) gives U23/U24/U25 a single
 * typed surface and one place that knows each path's shape. Only the endpoints
 * U22 needs are exhaustively wrapped; the raw `api` client remains available
 * for everything else in `client.ts`.
 */

import { api, unwrap, ApiError } from "./client";
import type {
  RunListResponse,
  UserResponse,
  MembershipResponse,
  BrandResponse,
  TokenResponse,
  ChangePasswordResponse,
  CreateBrandResponse,
  ProvisionUserRequest,
  Role,
  RunDetail,
  RunCreate,
  RunConfirm,
  ControlResponse,
  InconsistencyReportResponse,
  MemberRosterEntry,
  UpdateMemberRequest,
  UserRosterEntry,
  UpdateUserRequest,
  PreviewTicketsResponse,
  NotionTicketRow,
  ProfilesResponse,
  CampaignsResponse,
  LandingPagesResponse,
  AdsetsResponse,
  LastRunDefaultsResponse,
  RunPreviewResponse,
  HygieneFlagsResponse,
  VideoSyncMode,
  VideoSyncModeResponse,
  VideoSyncResponse,
  VideoTicketPerformance,
  HygieneScanResponse,
  BookkeepDebtsResponse,
  CoverageListResponse,
  CoverageSnapshotResponse,
  CoverageSyncAccepted,
  CoverageSyncStatusResponse,
  ConceptAliasRow,
  ConceptAliasListResponse,
  TeachConceptAliasRequest,
  ConceptAliasSuggestResponse,
  SavedReportRow,
  SavedReportListResponse,
} from "./client";

/* ---- Auth ------------------------------------------------------------- */

export async function login(
  email: string,
  password: string,
  brandId?: string,
): Promise<TokenResponse> {
  return unwrap(
    await api.POST("/auth/login", {
      body: { email, password, brand_id: brandId ?? null },
    }),
  );
}

export async function switchBrand(brandId: string): Promise<TokenResponse> {
  return unwrap(
    await api.POST("/auth/switch-brand", { body: { brand_id: brandId } }),
  );
}

/**
 * Super-admin only: provision a brand + its first admin in one call. The admin
 * is created with the initial password and `must_change_password=true`.
 */
export async function createBrand(args: {
  name: string;
  admin_email: string;
  admin_password: string;
}): Promise<CreateBrandResponse> {
  return unwrap(await api.POST("/auth/brands", { body: args }));
}

/**
 * Active-brand admin only: provision a user in the caller's brand with an
 * initial password + forced first-login change. 409 if the email is already a
 * member of this brand.
 */
export async function createUser(args: {
  email: string;
  role: Role;
  password: string;
}): Promise<UserResponse> {
  return unwrap(
    await api.POST("/auth/users", {
      body: args satisfies ProvisionUserRequest,
    }),
  );
}

/**
 * Change the caller's own password. `current_password` is always required
 * (admin-provisioned passwords are final — no forced-change bypass). Re-mints a
 * fresh token on success.
 */
export async function changePassword(args: {
  current_password: string;
  new_password: string;
}): Promise<ChangePasswordResponse> {
  return unwrap(
    await api.POST("/auth/change-password", {
      body: {
        current_password: args.current_password,
        new_password: args.new_password,
      },
    }),
  );
}

export async function getMe(): Promise<UserResponse> {
  return unwrap(await api.GET("/auth/me"));
}

export async function getMyMemberships(): Promise<MembershipResponse[]> {
  return unwrap(await api.GET("/auth/me/memberships"));
}

/* ---- User management (members, active-brand admin) ------------------- */

/**
 * Active-brand admin: list this brand's members (`user_id`, `email`, `role`,
 * membership-level `disabled`). The brand is the caller's verified active
 * membership server-side. 403 for a non-admin.
 */
export async function listMembers(): Promise<MemberRosterEntry[]> {
  return unwrap(await api.GET("/auth/members"));
}

/**
 * Active-brand admin: change a member's `role` and/or membership `disabled`
 * (≥1 required). Returns the updated member. 404 if not a member of this brand;
 * 409 on the last-admin / self-lockout guard.
 */
export async function updateMember(
  userId: string,
  body: UpdateMemberRequest,
): Promise<MemberRosterEntry> {
  return unwrap(
    await api.PATCH("/auth/members/{user_id}", {
      params: { path: { user_id: userId } },
      body,
    }),
  );
}

/**
 * Active-brand admin: remove the member's MEMBERSHIP in the active brand (not
 * the user). 204 on success; 409 on the last-admin guard; 404 if not a member.
 */
export async function removeMember(userId: string): Promise<void> {
  const { error, response } = await api.DELETE("/auth/members/{user_id}", {
    params: { path: { user_id: userId } },
  });
  // 204 No Content: openapi-fetch returns no data; only throw on error.
  if (error !== undefined) throw new ApiError(response.status, error);
}

/* ---- User management (all users, super-admin) ------------------------- */

/**
 * Super-admin: list ALL users (`id`, `email`, `is_super_admin`, global
 * `disabled`, `brand_count`). 403 for a non-super-admin.
 */
export async function listUsers(): Promise<UserRosterEntry[]> {
  return unwrap(await api.GET("/auth/users"));
}

/**
 * Super-admin: toggle a user's GLOBAL `disabled` flag (blocks login). Returns the
 * updated user. 409 when disabling a super-admin or oneself.
 */
export async function updateUser(
  userId: string,
  body: UpdateUserRequest,
): Promise<UserRosterEntry> {
  return unwrap(
    await api.PATCH("/auth/users/{user_id}", {
      params: { path: { user_id: userId } },
      body,
    }),
  );
}

/* ---- Runs ------------------------------------------------------------- */

export async function listRuns(limit = 20, offset = 0): Promise<RunListResponse> {
  return unwrap(await api.GET("/runs", { params: { query: { limit, offset } } }));
}

export async function getRun(runId: string): Promise<RunDetail> {
  return unwrap(
    await api.GET("/runs/{run_id}", { params: { path: { run_id: runId } } }),
  );
}

export async function createRun(body: RunCreate): Promise<RunDetail> {
  return unwrap(await api.POST("/runs", { body }));
}

/* ---- Video ticket sync (2026-10-12) ------------------------------------ */

/** The video sync mode + the active brand's video tickets (viewer). */
export async function getVideoSync(): Promise<VideoSyncResponse> {
  return unwrap(await api.GET("/video-sync"));
}

/** How a launched video is doing (2026-10-22). */
export async function getVideoTicketPerformance(id: string, today = false): Promise<VideoTicketPerformance> {
  return unwrap(
    await api.GET("/video-sync/tickets/{id}/performance", {
      params: { path: { id }, query: today ? { today: true } : {} },
    }),
  );
}

/** Switch the server-wide video sync mode (super-admin). */
export async function setVideoSyncMode(mode: VideoSyncMode): Promise<VideoSyncModeResponse> {
  return unwrap(await api.PUT("/video-sync/mode", { body: { mode } }));
}

/** The Notion webhook's token and health (2026-11-03, super-admin). */
export async function getNotionWebhook() {
  return unwrap(await api.GET("/video-sync/webhook"));
}

/** Forget the Notion webhook token so Notion can be verified again (super-admin). */
export async function resetNotionWebhook() {
  return unwrap(await api.DELETE("/video-sync/webhook"));
}

/** Send one product's stuck-ticket digest now, as a test, to its recipient (2026-10-30). */
export async function sendDigestTest(productKey: string): Promise<{ recipient: string; stuck: number }> {
  return unwrap(await api.POST("/video-sync/digest-test", { body: { product_key: productKey } }));
}

/* ---- Hygiene scan (plan 2026-07-02-001) -------------------------------- */

/** The active brand's currently-flagged tickets (viewer). */
export async function getHygieneFlags(): Promise<HygieneFlagsResponse> {
  return unwrap(await api.GET("/hygiene/flags"));
}

/** Enqueue an immediate hygiene scan of the active brand (operator). */
export async function triggerHygieneScan(): Promise<HygieneScanResponse> {
  return unwrap(await api.POST("/hygiene/scan"));
}

/** The brand's open truth-debts (batch 5 #8, viewer) — empty IS healthy. */
export async function getBookkeepDebts(): Promise<BookkeepDebtsResponse> {
  return unwrap(await api.GET("/hygiene/debts"));
}

/** Dismiss a debt resolved by hand (operator). */
export async function dismissBookkeepDebt(debtId: string): Promise<void> {
  const { error, response } = await api.DELETE("/hygiene/debts/{debt_id}", {
    params: { path: { debt_id: debtId } },
  });
  // 204 No Content — see deleteConceptAlias for the response-cast rationale.
  if (error !== undefined) throw new ApiError((response as Response).status, error);
}

/* ---- Concept-alias ledger (batch 5 #7) --------------------------------- */

/** The brand's taught concept aliases, optionally one product's (viewer). */
export async function listConceptAliases(
  productKey?: string,
): Promise<ConceptAliasListResponse> {
  return unwrap(
    await api.GET("/concept-aliases", {
      params: { query: productKey ? { product_key: productKey } : {} },
    }),
  );
}

/** Teach (or re-teach) one ticket-concept → sheet-CONCEPT alias (operator). */
export async function teachConceptAlias(
  body: TeachConceptAliasRequest,
): Promise<ConceptAliasRow> {
  return unwrap(await api.POST("/concept-aliases", { body }));
}

/** Forget a taught alias (operator). */
export async function deleteConceptAlias(aliasId: string): Promise<void> {
  const { error, response } = await api.DELETE("/concept-aliases/{alias_id}", {
    params: { path: { alias_id: aliasId } },
  });
  // 204 No Content: openapi-fetch returns no data; only throw on error. The op
  // declares no error content, so openapi-fetch types `response` as never —
  // cast to the real Response for the status.
  if (error !== undefined) throw new ApiError((response as Response).status, error);
}

/**
 * Sheet-backed teach candidates for one concept (operator): closest rows first,
 * each with the BATCH/AD_NAMING/AD_FORMAT the confirm dialog shows, plus what
 * the matcher currently resolves. 400 = no master sheet configured; 424 = the
 * google_sa credential is missing; 502 = the sheet is unreadable.
 */
export async function suggestConceptAlias(args: {
  productKey: string;
  concept: string;
}): Promise<ConceptAliasSuggestResponse> {
  return unwrap(
    await api.GET("/concept-aliases/suggest", {
      params: {
        query: { product_key: args.productKey, concept: args.concept },
      },
    }),
  );
}

/* ---- Creative Analysis coverage (plan 2026-07-22-001, U7) --------------- */

/**
 * Every product of the active brand + its latest snapshot summary (viewer).
 * A product the daily job has never snapshotted carries null snapshot fields
 * — the page's per-product empty state, not an error.
 */
export async function getCoverageList(): Promise<CoverageListResponse> {
  return unwrap(await api.GET("/coverage"));
}

/**
 * The latest snapshot's FULL explorer payload for one product (viewer).
 * 404 = no snapshot yet (the documented U5 contract) — callers treat it as
 * the empty state, never an error surface.
 */
export async function getCoverageSnapshot(
  productId: string,
): Promise<CoverageSnapshotResponse> {
  return unwrap(
    await api.GET("/coverage/{product_id}", {
      params: { path: { product_id: productId } },
    }),
  );
}

/* ---- Saved reports (2026-10-28) ---------------------------------------- */

/** Every saved report for a product (viewer). */
export async function listSavedReports(productId: string): Promise<SavedReportListResponse> {
  return unwrap(await api.GET("/saved-reports", { params: { query: { product_id: productId } } }));
}

/** Save the current view under a name (viewer; 409 when the name is taken). */
export async function createSavedReport(body: {
  product_id: string;
  name: string;
  state: Record<string, unknown>;
}): Promise<SavedReportRow> {
  return unwrap(await api.POST("/saved-reports", { body }));
}

/** Rename or re-save a report (its creator or a super-admin). */
export async function updateSavedReport(
  reportId: string,
  body: { name?: string; state?: Record<string, unknown> },
): Promise<SavedReportRow> {
  return unwrap(
    await api.PATCH("/saved-reports/{report_id}", { params: { path: { report_id: reportId } }, body }),
  );
}

/** Delete a report (its creator or a super-admin). */
export async function deleteSavedReport(reportId: string): Promise<void> {
  const { error, response } = await api.DELETE("/saved-reports/{report_id}", {
    params: { path: { report_id: reportId } },
  });
  // 204 No Content — see deleteConceptAlias for the response-cast rationale.
  if (error !== undefined) throw new ApiError((response as Response).status, error);
}

/** Draft a Pipeline ticket from a whitespace cell (scaffolder, 2026-07-29 —
 *  operator, 201). Lands in Ideation as DRAFT_…; returns the page URL. */
export async function postCoverageBrief(body: {
  product_id: string;
  angle: string;
  avatar: string;
  awareness: string;
  note?: string;
}): Promise<{ created: boolean; page_id: string; url: string | null }> {
  return unwrap(await api.POST("/coverage/brief", { body }));
}

/** Live recompute state (2026-08-18): idle | queued | running | failed for
 *  the brand's sync lanes. `product_id` reads that product's lane first,
 *  falling back to the brand-wide lane. Polled after a Recompute click so a
 *  slow sync and a silently failed one stop looking identical. */
export async function getCoverageSyncStatus(
  productId?: string,
): Promise<CoverageSyncStatusResponse> {
  return unwrap(
    await api.GET("/coverage/sync/status", {
      params: { query: productId ? { product_id: productId } : {} },
    }),
  );
}

/** Enqueue an immediate coverage sync of the active brand (202).
 *  `start`/`end` (2026-07-26) recompute for THAT live window — both bounds
 *  YYYY-MM-DD, validated server-side; omitted = the default window.
 *  `product_id` (2026-08-04, fast recompute) syncs ONLY that product;
 *  omitted = the whole brand (the All-products view). */
export async function triggerCoverageSync(opts?: {
  start?: string;
  end?: string;
  product_id?: string;
}): Promise<CoverageSyncAccepted> {
  return unwrap(
    await api.POST("/coverage/sync", { body: opts ?? {} }),
  );
}

/* ---- Launch wizard ---------------------------------------------------- */

/**
 * Live Notion ticket preview for the launch wizard (operator). Resolves the
 * product by key (the SPA addresses products by key) and returns the live
 * "Ready to Launch" tickets with per-row pre-check verdicts. An empty `flow`
 * means "no flow filter" — so it is omitted from the query entirely. The
 * Notion credential is resolved server-side; the fetch may surface a 424/502
 * Notion-auth error that the caller turns into an actionable retry message.
 */
export async function previewLaunchTickets(args: {
  productKey: string;
  flow?: string;
  format?: string;
}): Promise<PreviewTicketsResponse> {
  const flow = args.flow?.trim();
  const format = args.format?.trim();
  return unwrap(
    await api.GET("/launch/tickets", {
      params: {
        query: {
          product_key: args.productKey,
          ...(flow ? { flow } : {}),
          ...(format ? { format } : {}),
        },
      },
    }),
  );
}

/**
 * Refresh-by-ids (Step 2): re-pull only the listed page ids and return fresh
 * rows in the same `NotionTicketRow` shape (created order + dedup verdicts
 * recomputed server-side). The SPA merges these into the preview cache in place.
 */
export async function refreshLaunchTickets(args: {
  productKey: string;
  pageIds: string[];
}): Promise<NotionTicketRow[]> {
  return unwrap(
    await api.POST("/launch/tickets/refresh", {
      body: { product_key: args.productKey, page_ids: args.pageIds },
    }),
  );
}

/**
 * Settings step (Admanage-backed): the brand's synced Facebook pages (cached
 * server-side 15 min). A 424 means the brand's Admanage credential needs
 * reconnecting; a 502 is a transient Admanage reach failure.
 */
export async function getLaunchProfiles(
  productKey: string,
): Promise<ProfilesResponse> {
  return unwrap(
    await api.GET("/launch/profiles", {
      params: { query: { product_key: productKey } },
    }),
  );
}

/** Settings step: the brand account's Active + Scheduled campaigns. */
export async function getLaunchCampaigns(
  productKey: string,
): Promise<CampaignsResponse> {
  return unwrap(
    await api.GET("/launch/campaigns", {
      params: { query: { product_key: productKey } },
    }),
  );
}

/** Settings step: the product's landing pages from the Notion Landing Pages
 *  table, for the URL field's dropdown (2026-10-19). */
export async function getLaunchLandingPages(
  productKey: string,
): Promise<LandingPagesResponse> {
  return unwrap(
    await api.GET("/launch/landing-pages", {
      params: { query: { product_key: productKey } },
    }),
  );
}

/** Settings step: the chosen campaign's ad sets (gated on a campaign choice). */
export async function getLaunchAdsets(
  productKey: string,
  campaignId: string,
): Promise<AdsetsResponse> {
  return unwrap(
    await api.GET("/launch/adsets", {
      params: { query: { product_key: productKey, campaign_id: campaignId } },
    }),
  );
}

/** Settings step: the last run's Settings for prefill (2026-07-07 #4). */
export async function getLastRunDefaults(
  productKey: string,
): Promise<LastRunDefaultsResponse> {
  return unwrap(
    await api.GET("/launch/last-defaults", {
      params: { query: { product_key: productKey } },
    }),
  );
}

/**
 * Preview step: the run's prepped per-ticket + per-ad payloads (what FINALIZE
 * will send to Meta) once the run has parked at `AWAITING_CONFIRM`. Carries the
 * run `status` so the SPA can distinguish "still prepping" from "ready".
 */
export async function getRunPreview(
  runId: string,
): Promise<RunPreviewResponse> {
  return unwrap(
    await api.GET("/runs/{run_id}/preview", {
      params: { path: { run_id: runId } },
    }),
  );
}

/**
 * Confirm a gated `AWAITING_CONFIRM` run. The optional body carries the
 * Preview-stage launch decision (U1/U9): `mode` ('PUBLISHED' default → push to
 * Meta; 'DRAFT' → stage in AdManage only) and `selected_ad_ids` (Published; omit
 * for "all"). An empty body is back-compatible (Published / all).
 */
export async function confirmRun(
  runId: string,
  body?: RunConfirm,
): Promise<ControlResponse> {
  return unwrap(
    await api.POST("/runs/{run_id}/confirm", {
      params: { path: { run_id: runId } },
      body,
    }),
  );
}

export async function pauseRun(runId: string): Promise<ControlResponse> {
  return unwrap(
    await api.POST("/runs/{run_id}/pause", {
      params: { path: { run_id: runId } },
    }),
  );
}

export async function resumeRun(runId: string): Promise<ControlResponse> {
  return unwrap(
    await api.POST("/runs/{run_id}/resume", {
      params: { path: { run_id: runId } },
    }),
  );
}

/** Re-drive a FAILED run from the stage that failed (batch 4, 2026-07-08). */
export async function redriveRun(runId: string): Promise<ControlResponse> {
  return unwrap(
    await api.POST("/runs/{run_id}/redrive", {
      params: { path: { run_id: runId } },
    }),
  );
}

export async function cancelRun(runId: string): Promise<ControlResponse> {
  return unwrap(
    await api.POST("/runs/{run_id}/cancel", {
      params: { path: { run_id: runId } },
    }),
  );
}

/** Super-admin only — hard-abort a wedged run (e.g. stuck in "cancelling"). */
export async function forceCancelRun(runId: string): Promise<ControlResponse> {
  return unwrap(
    await api.POST("/runs/{run_id}/force-cancel", {
      params: { path: { run_id: runId } },
    }),
  );
}

export async function getRunInconsistencies(
  runId: string,
): Promise<InconsistencyReportResponse> {
  return unwrap(
    await api.GET("/runs/{run_id}/inconsistencies", {
      params: { path: { run_id: runId } },
    }),
  );
}

/* ---- Brand directory (membership labels) ----------------------------- */

/**
 * Each membership now carries the brand's display `brand_name` (from
 * `/auth/me/memberships`), so the brand switcher renders the name directly.
 * This helper resolves a `BrandResponse` to a display label (name, id fallback).
 */
export function brandLabel(t: Pick<BrandResponse, "id" | "name">): string {
  return t.name || t.id;
}
